import React from "react";
import { isEmpty, pick, keys } from "lodash";
import NoteForm from "./NoteForm";
import NoteList from "./NoteList";
import Button from "./CustomButton";
import Icon from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/fontawesome-free-solid";
import {
  compose,
  withHandlers,
  setDisplayName,
  lifecycle,
  branch,
  renderNothing,
  withProps,
  renderComponent
} from "recompose";
import { connect } from "react-redux";
import { actions } from "../modules/notes";

const mapStateToProps = state => ({
  submittingNote: state.notes.submittingNote,
  failSubmitNote: state.notes.failSubmitNote,
  successSubmitNote: state.notes.successSubmitNote,
  currentDealer: state.dealers.currentDealer,
  currentContract: state.contracts.currentContract,
  currentNote: state.notes.currentNote,
  notes: state.notes.notes,
  role: state.settings.role
});

const mapDispatchToProps = {
  createNoteAsync: actions.createNoteAsync,
  createNoteSync: actions.createNoteSync,
  updateNoteSync: actions.updateNoteSync,
  updateNoteAsync: actions.updateNoteAsync,
  setCurrentNote: actions.setCurrentNote,
  deleteNoteSync: actions.deleteNoteSync,
  deleteNoteAsync: actions.deleteNoteAsync,
  resetState: actions.resetState
};

const withState = connect(
  mapStateToProps,
  mapDispatchToProps
);

const withHandleSubmit = withHandlers({
  onSubmit: ({
    createNoteAsync,
    createNoteSync,
    updateNoteAsync,
    updateNoteSync,
    currentDealer,
    currentContract,
    currentNote
  }) => (data, { resetForm }) => {
    const clearForm = () => {
      resetForm({ content: "", images: [], videos: [] });
    };
    if (currentNote) {
      if (currentContract) {
        updateNoteAsync({
          data,
          dealer_id: currentContract.dealer.id,
          contract_id: currentContract.id,
          note_id: currentNote.id
        }).then(clearForm);
      } else {
        updateNoteSync(data);
        clearForm();
      }
    } else {
      if (currentContract) {
        createNoteAsync({
          data,
          dealer_id: currentContract.dealer.id,
          contract_id: currentContract.id
        }).then(clearForm);
      } else {
        createNoteSync(data);
        clearForm();
      }
    }
  },
  clearNote: ({ setCurrentNote }) => e => {
    setCurrentNote(null);
  }
});

const NotesEnhacer = compose(
  setDisplayName("Notes"),
  withState,
  withHandlers({
    onClickUpdate: ({ setCurrentNote }) => note => ev => {
      setCurrentNote(note);
    },
    onClickDelete: ({
      currentContract,
      deleteNoteAsync,
      deleteNoteSync
    }) => note => ev => {
      if (window.confirm("Do you really want to delete this note?")) {
        if (currentContract) {
          deleteNoteAsync({
            dealer_id: currentContract.dealer.id,
            contract_id: currentContract.id,
            note_id: note.id
          });
        } else {
          deleteNoteSync(note);
        }
      }
    }
  })
);

const isCustomer = ({ role }) => role !== "staff";

const EmptyNotes = () => (
  <h4 className="text-muted">There are no available notes</h4>
);

const NotesTable = compose(
  setDisplayName("NotesTable"),
  NotesEnhacer,
  branch(({ notes }) => isEmpty(notes), renderComponent(EmptyNotes))
)(({ notes, onClickDelete, onClickUpdate, submittingNote, role }) => (
  <NoteList notes={notes || []}>
    {({ note }) =>
      role === "staff" && (
        <div className="row justify-content-end">
          <div className="">
            <Button
              color="info"
              simple
              disabled={submittingNote}
              onClick={onClickUpdate(note)}
            >
              <Icon icon={faEdit} />
            </Button>
          </div>
          <div className="">
            <Button
              color="info"
              simple
              disabled={submittingNote}
              onClick={onClickDelete(note)}
            >
              <Icon icon={faTrash} />
            </Button>
          </div>
        </div>
      )
    }
  </NoteList>
));

const EnhancedForm = compose(
  setDisplayName("EnhancedNoteForm"),
  withState,
  branch(isCustomer, renderNothing),
  withHandleSubmit,
  withProps(({ currentNote }) => ({
    initialValues: noteInitialValues(currentNote)
  }))
)(NoteForm);

const enhanced = compose(
  withState,
  setDisplayName("NoteSection"),
  lifecycle({
    componentWillUnmount() {
      this.props.resetState();
    }
  })
);

const NoteSection = enhanced(() => {
  return (
    <div className="">
      <EnhancedForm />
      <NotesTable />
    </div>
  );
});

const noteInitialValues = currentNote => {
  let initialValues = { content: "", images: [], videos: [] };
  return isEmpty(currentNote)
    ? initialValues
    : pick(currentNote, keys(initialValues));
};

export default NoteSection;
